import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, User, ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { NAV } from "../mock";
import { useCart } from "../context/CartContext";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2 select-none" aria-label="NutExpert home">
    <div className="relative">
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="28" cy="28" r="27" stroke="#c8232c" strokeWidth="2" />
        <path d="M28 14 C20 18, 18 28, 24 36 C20 30, 24 22, 28 20 C32 22, 36 30, 32 36 C38 28, 36 18, 28 14 Z" fill="#c8232c"/>
        <path d="M28 36 L28 44" stroke="#c8232c" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    </div>
    <div className="leading-none">
      <div className="text-[11px] tracking-[0.3em] text-stone-500">N° EXPERT</div>
      <div className="text-2xl font-light text-stone-800">
        Nut<span className="font-bold text-stone-900">Expert</span>
      </div>
    </div>
  </Link>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const navigate = useNavigate();
  const { count, openCart } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onAnchor = (href) => {
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (href.startsWith("#")) {
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else navigate("/" + href);
    }
  };

  return (
    <>
      <div className="announce w-full py-2 text-center">
        FREE DELIVERY IN EUROPE FROM €49 · NEW HARVEST 2025 IN STOCK
      </div>
      <header
        className={`sticky top-0 z-40 bg-white transition-shadow ${
          scrolled ? "shadow-[0_2px_24px_rgba(0,0,0,0.06)]" : ""
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[88px] flex items-center justify-between gap-6">
          <Logo />
          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button
                  onClick={() => onAnchor(item.href)}
                  className="nav-link font-medium flex items-center gap-1 uppercase"
                >
                  {item.label}
                  {item.children && <ChevronDown size={14} />}
                </button>
                {item.children && openMenu === item.label && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50">
                    <div className="min-w-[240px] bg-white border border-stone-200 shadow-xl py-2">
                      {item.children.map((c) => (
                        <button
                          key={c.label}
                          onClick={() => onAnchor(c.href)}
                          className="block w-full text-left px-5 py-2.5 text-sm text-stone-700 hover:text-[#c8232c] hover:bg-stone-50"
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <button className="text-stone-700 hover:text-[#c8232c]" aria-label="Search">
              <Search size={20} />
            </button>
            <button className="text-stone-700 hover:text-[#c8232c] hidden md:inline-flex" aria-label="Wishlist">
              <Heart size={20} />
            </button>
            <button className="text-stone-700 hover:text-[#c8232c] hidden md:inline-flex" aria-label="Account">
              <User size={20} />
            </button>
            <button onClick={openCart} className="relative text-stone-700 hover:text-[#c8232c]" aria-label="Cart">
              <ShoppingBag size={20} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#c8232c] text-white text-[10px] rounded-full w-[18px] h-[18px] flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button
              className="lg:hidden text-stone-700"
              onClick={() => setMobileOpen(true)}
              aria-label="Menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="h-[88px] flex items-center justify-between px-6 border-b border-stone-200">
            <Logo />
            <button onClick={() => setMobileOpen(false)} className="text-stone-700">
              <X size={24} />
            </button>
          </div>
          <div className="p-6 overflow-y-auto">
            {NAV.map((item) => (
              <div key={item.label} className="py-3 border-b border-stone-100">
                <button
                  onClick={() => {
                    onAnchor(item.href);
                    setMobileOpen(false);
                  }}
                  className="font-medium text-base uppercase tracking-wider"
                >
                  {item.label}
                </button>
                {item.children && (
                  <div className="mt-2 pl-3">
                    {item.children.map((c) => (
                      <button
                        key={c.label}
                        onClick={() => {
                          onAnchor(c.href);
                          setMobileOpen(false);
                        }}
                        className="block py-2 text-sm text-stone-600"
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
